import { V2EmptyState } from "@stacklet/ui";
import { ConnectionHandler, graphql, useLazyLoadQuery } from "react-relay";
import { generatePath, useNavigate, useSearchParams } from "react-router";

import { POLICY_COLLECTIONS_BREADCRUMB } from "app/constants/breadcrumbs";
import { paths } from "app/constants/paths";
import { useAlertContext } from "app/contexts";
import { usePolicyCollectionFilterElement } from "app/hooks/relay/usePolicyCollectionFilterElement";
import { useProblemsAlert } from "app/hooks/ui/useProblemsAlert";
import { AdminPage } from "app/views/admin/AdminPage";

import { UpsertDpcForm } from "../components/UpsertDpcForm";
import { type CreateDpcRootDeployQuery } from "./__generated__/CreateDpcRootDeployQuery.graphql";
import { type CreateDpcRootQuery } from "./__generated__/CreateDpcRootQuery.graphql";

export function CreateDpcRoot() {
  const { alertDispatch } = useAlertContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams({});
  const uuid = searchParams.get("repositoryUUID") || "";

  const data = useLazyLoadQuery<CreateDpcRootQuery>(
    graphql`
      query CreateDpcRootQuery(
        $uuid: String!
        $accountGroupFilterElement: FilterElementInput
      ) {
        __id
        ...AdminPage_query
        repositoryConfig(uuid: $uuid) {
          repositoryConfig {
            uuid
          }
          ...useProblemsAlert_problems
        }
        ...UpsertDpcForm_query
      }
    `,
    {
      uuid: uuid,
    },
  );

  const agFilterElement = {
    single: {
      name: "can-deploy-policies",
      operator: "equals",
      value: "true",
    },
  };

  const dataDeploy = useLazyLoadQuery<CreateDpcRootDeployQuery>(
    graphql`
      query CreateDpcRootDeployQuery($agFilterElement: FilterElementInput) {
        accountGroups(filterElement: $agFilterElement) {
          pageInfo {
            total
          }
          ...useProblemsAlert_problems
        }
      }
    `,
    {
      agFilterElement,
    },
  );

  useProblemsAlert(data.repositoryConfig);
  useProblemsAlert(dataDeploy.accountGroups);

  function onAdded(message: string, uuid: string) {
    navigate(
      generatePath(paths.adminPolicyCollectionDetails, { collectionId: uuid }),
    );
    alertDispatch({
      message,
      severity: "success",
    });
  }

  const filterElement = usePolicyCollectionFilterElement();

  const connectionId = ConnectionHandler.getConnectionID(
    data?.__id,
    "PolicyCollections_policyCollections",
    { filterElement },
  );
  const repositoryConfig = data?.repositoryConfig?.repositoryConfig;
  const repositoryUUID = repositoryConfig?.uuid;

  const canDeployPolicies =
    (dataDeploy?.accountGroups.pageInfo?.total ?? 0) > 0;

  if (!repositoryUUID) {
    return (
      <V2EmptyState message="No policy repository found to create this dynamic policy collection" />
    );
  }

  return (
    <AdminPage
      parentCrumbs={[POLICY_COLLECTIONS_BREADCRUMB]}
      queryRef={data}
      title="Create Dynamic Policy Collection"
      isForm
    >
      <UpsertDpcForm
        canDeployPolicies={canDeployPolicies}
        connectionId={connectionId}
        onAdded={(message, uuid) => onAdded(message, uuid)}
        onCancel={() =>
          navigate(
            generatePath(paths.adminRepositoryDetails, { repositoryUUID }),
          )
        }
        queryRefAcl={data}
        repositoryUUID={repositoryUUID}
      />
    </AdminPage>
  );
}
